import { ref, computed, onUnmounted, getCurrentInstance } from 'vue'

const isOnline = ref<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true)
const wasOffline = ref<boolean>(false)
const isLieFi = ref<boolean>(false)
const isManualOffline = ref<boolean>(
  typeof localStorage !== 'undefined' && localStorage.getItem('manual_offline_mode') === 'true'
)

const effectiveOffline = computed(() => !isOnline.value || isLieFi.value || isManualOffline.value)

let globalListenersAttached = false
let isFirestoreNetworkDisabled = false
let consecutiveProbeFailures = 0

// Control Firestore network without throwing in test environments
export const syncFirestoreNetworkState = async (offline: boolean) => {
  if (import.meta.env?.MODE === 'test') {
    return
  }

  try {
    const [{ disableNetwork, enableNetwork }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('../firebase')
    ])
    if (offline && !isFirestoreNetworkDisabled) {
      isFirestoreNetworkDisabled = true
      await disableNetwork(db)
    } else if (!offline && isFirestoreNetworkDisabled) {
      isFirestoreNetworkDisabled = false
      await enableNetwork(db)
    }
  } catch {
    // Gracefully ignore if already in state
  }
}

// Active ping probe to detect Lie-Fi (connection reported online, but packets dropping / hanging)
export const checkConnection = async (forceInTest: boolean = false): Promise<boolean> => {
  if (typeof window === 'undefined') return true
  if (import.meta.env?.MODE === 'test' && !forceInTest) return true
  if (!isOnline.value || isManualOffline.value) return false

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 2500)

  try {
    const res = await fetch(`/favicon.png?_ping=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal
    })
    const isHealthy = res.ok || res.status < 400
    if (isHealthy) {
      consecutiveProbeFailures = 0
      if (isLieFi.value) {
        isLieFi.value = false
        syncFirestoreNetworkState(effectiveOffline.value)
      }
      return true
    } else {
      consecutiveProbeFailures++
    }
  } catch {
    consecutiveProbeFailures++
  } finally {
    clearTimeout(timer)
  }

  if (consecutiveProbeFailures >= 2) {
    if (!isLieFi.value) {
      isLieFi.value = true
      syncFirestoreNetworkState(true)
    }
    return false
  }

  return !isLieFi.value
}

export function useNetworkState(onOnlineRestored?: () => void) {
  const updateOnline = () => {
    const prevOffline = wasOffline.value || effectiveOffline.value
    isOnline.value = true
    consecutiveProbeFailures = 0
    isLieFi.value = false

    syncFirestoreNetworkState(effectiveOffline.value)

    if (prevOffline && !effectiveOffline.value) {
      if (onOnlineRestored) {
        onOnlineRestored()
      }
      wasOffline.value = false
    }

    checkConnection()
  }

  const updateOffline = () => {
    isOnline.value = false
    wasOffline.value = true
    syncFirestoreNetworkState(true)
  }

  const setManualOffline = (enabled: boolean) => {
    const prevEffective = effectiveOffline.value
    isManualOffline.value = enabled
    try {
      localStorage.setItem('manual_offline_mode', enabled ? 'true' : 'false')
    } catch {}

    syncFirestoreNetworkState(effectiveOffline.value)

    if (prevEffective && !effectiveOffline.value && onOnlineRestored) {
      onOnlineRestored()
    }
  }

  if (!globalListenersAttached && typeof window !== 'undefined') {
    window.addEventListener('online', updateOnline)
    window.addEventListener('offline', updateOffline)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkConnection()
      }
    })

    if (import.meta.env?.MODE !== 'test') {
      setInterval(() => {
        checkConnection()
      }, 30000)
    }

    globalListenersAttached = true
  }

  if (getCurrentInstance()) {
    onUnmounted(() => {
      // Keep shared global state alive across component unmounts
    })
  }

  return {
    isOnline,
    isLieFi,
    isManualOffline,
    effectiveOffline,
    wasOffline,
    updateOnline,
    updateOffline,
    setManualOffline,
    checkConnection
  }
}
